import UIfx from "uifx";

import type { TUserAuthData } from "@storage/session/types";
import { userAuthDataStorage } from "@storage/sessionStorage";

import { DELAY_TIME_7000 } from "@configs/constants";

let privateAudioInterval: ReturnType<typeof setInterval> | undefined = undefined;
const AUDIO_OPTIONS = {
	volume: 0.0,
	throttleMs: 100,
};
const audioCache = new Map<string, UIfx>();

const getAudio = (url: string) => {
	const cachedAudio = audioCache.get(url);

	if (cachedAudio) {
		return cachedAudio;
	}

	const audio = new UIfx(url, AUDIO_OPTIONS);
	audioCache.set(url, audio);

	return audio;
};

const setMute = () => {
	const currentAccount = userAuthDataStorage.getItemAsJson<TUserAuthData>();
	if (currentAccount?.app_mute) {
		return 0.0;
	} else {
		return 0.1;
	}
};

export const cancelSong = (): void => {
	clearInterval(privateAudioInterval);
	privateAudioInterval = undefined;
};

/** This is why you can see multiple calls to the same file in the network tab of the console:
 * A single Audio playback can trigger multiple range reads: one for headers/metadata, then more chunks for decoding/buffering.
 * Since entries show from disk cache, these are mostly cache hits logged as requests, not real repeated downloads.
 */
const handlePlayAudio = (mute: number, amount: number | null) => ({
	tipsSent: () => {
		if (amount === null) return;

		if (amount >= 1 && amount <= 19) {
			const audioFileTipsSentMinorPlay = getAudio("https://mpassets.dreamcamtrue.com/audioFileTipsSentMinor.wav");
			audioFileTipsSentMinorPlay.setVolume(mute).play();
		} else if (amount >= 20 && amount <= 99) {
			const audioFileTipsSentMediumPlay = getAudio("https://mpassets.dreamcamtrue.com/audioFileTipsSentMedium.wav");
			audioFileTipsSentMediumPlay.setVolume(mute).play();
		} else if (amount >= 99) {
			const audioFileTipsSentHighPlay = getAudio("https://mpassets.dreamcamtrue.com/audioFileTipsSentHigh.wav");
			audioFileTipsSentHighPlay.setVolume(mute).play();
		} else {
			const audioFileTipsSentMediumPlay = getAudio("https://mpassets.dreamcamtrue.com/audioFileTipsSentMedium.wav");
			audioFileTipsSentMediumPlay.setVolume(mute).play();
		}
	},
	complimentSent: () => audioFileTipsMenuSent.setVolume(mute).play(),
	tipMenuPurchased: () => audioFileTipsMenuSent.setVolume(mute).play(),
	toysMenuItemPurchased: () => audioFileTipsMenuSent.setVolume(mute).play(),
	broadcastPrivateModeRequestCreatedByClient: () => {
		clearInterval(privateAudioInterval);
		const audioFilePrivateRequestPlay = getAudio("https://mpassets.dreamcamtrue.com/audioFilePrivateRequest.wav");
		audioFilePrivateRequestPlay.setVolume(mute).play();
		setTimeout(() => {
			if (privateAudioInterval) {
				clearInterval(privateAudioInterval);
			}
		}, 21000);
		privateAudioInterval = setInterval(() => {
			audioFilePrivateRequestPlay.setVolume(mute).play();
		}, DELAY_TIME_7000);
	},
	broadcastPrivateModeEnabledForModel: () => {
		clearInterval(privateAudioInterval);
		const audioFilePrivateEnabledPlay = getAudio("https://mpassets.dreamcamtrue.com/audioFilePrivateEnabled.mp3");
		audioFilePrivateEnabledPlay.setVolume(mute).play();
	},
	broadcastPrivateModeDisabledByClient: () => audioFilePrivateDisabled.setVolume(mute).play(),
	broadcastPrivateModeDisabledByModel: () => audioFilePrivateDisabled.setVolume(mute).play(),
	broadcastPrivateModeDisabledByBilling: () => audioFilePrivateDisabled.setVolume(mute).play(),
	broadcastPrivateModeCanceledDueToTechnicalReasons: () => audioFilePrivateDisabled.setVolume(mute).play(),
	broadcastPrivateModeRequestRefusedByClient: () => clearInterval(privateAudioInterval),
	broadcastPrivateModeRequestRefusedByBilling: () => clearInterval(privateAudioInterval),
	broadcastPrivateModeRequestRefusedByModel: () => clearInterval(privateAudioInterval),
	broadcastPrivateModeRequestRefusedByModelForModel: () => clearInterval(privateAudioInterval),
	broadcastPrivateRequestCanceledDueToMaintenanceReasons: () => clearInterval(privateAudioInterval),
	broadcastGoalEnded: () => {
		const audioGoalEndedPlay = getAudio("https://mpassets.dreamcamtrue.com/audioGoalEnded.wav");
		audioGoalEndedPlay.setVolume(mute).play();
	},
});

const audioFileTipsMenuSent = getAudio("https://mpassets.dreamcamtrue.com/audioFileTipsMenuSent.wav");

const audioFilePrivateDisabled = getAudio("https://mpassets.dreamcamtrue.com/audioFilePrivateDisabled.mp3");

export const playAudio = (reason: string, amount: number | null) => {
	const sound = setMute();

	const HANDLERS = handlePlayAudio(sound, amount);
	const handler = HANDLERS[reason as keyof ReturnType<typeof handlePlayAudio>];
	handler();
};
